/**
 * Static SEO page registry for AutoMapa.
 * Milestone 26: First SEO content set (Medellín / Antioquia).
 * TODO(CMS): /admin/content/seo-pages editor.
 */
import {
  SEO_PAGE_TYPES,
  SEO_CITIES,
  SEO_POPULAR_MAKES,
  SEO_BUDGET_RANGES,
  SEO_BODY_TYPES,
  SEO_NEARBY_CITIES,
  SEO_M26_MAJOR_CITY_SLUGS,
} from "../constants/seoPageConstants";
import {
  M26_ANTIOQUIA_CITY_SLUGS,
  M26_MEDELLIN_BRAND_SLUGS,
  M26_MEDELLIN_MODELS,
  M26_MEDELLIN_BUDGET_SLUGS,
  M26_GUIDES,
  CITY_INTRO_COPY,
  CITY_META_DESCRIPTIONS,
  BODY_TYPE_MEDELLIN_COPY,
  BRAND_MEDELLIN_INTRO,
  MODEL_MEDELLIN_INTRO,
  BUDGET_MEDELLIN_GUIDANCE,
  FINANCING_DISCLAIMER,
  commonFaqs,
} from "./seoPageContentM26";

const NOW = "2026-06-16T12:00:00.000Z";
const MEDELLIN = SEO_CITIES.find((c) => c.slug === "medellin");

function baseConfig(overrides) {
  return {
    faqItems: [],
    guideLinks: [],
    internalLinks: [],
    inventoryFilters: {},
    financingRequired: false,
    transmission: null,
    noindex: false,
    includeInSitemap: true,
    priority: 0.6,
    approved: true,
    indexWhenEmpty: false,
    createdAt: NOW,
    updatedAt: NOW,
    ...overrides,
  };
}

function findCity(slug) {
  return SEO_CITIES.find((c) => c.slug === slug);
}

function findMake(slug) {
  return SEO_POPULAR_MAKES.find((m) => m.slug === slug);
}

function findBudget(slug) {
  return SEO_BUDGET_RANGES.find((b) => b.slug === slug);
}

function buildCityPage(city) {
  const path = `/carros-usados-${city.slug}`;
  const isMajor = SEO_M26_MAJOR_CITY_SLUGS.has(city.slug);
  return baseConfig({
    id: `city-${city.slug}`,
    pageType: SEO_PAGE_TYPES.CITY,
    slug: city.slug,
    path,
    title: `Carros usados en ${city.name}`,
    h1: `Carros usados en ${city.name}`,
    metaTitle: `Carros usados en ${city.name} | AutoMapa`,
    metaDescription: CITY_META_DESCRIPTIONS[city.slug] || `Encuentra carros usados en ${city.name} en AutoMapa.`,
    introCopy: CITY_INTRO_COPY[city.slug] || `Explora carros usados en ${city.name} con AutoMapa.`,
    city: city.name,
    citySlug: city.slug,
    inventoryFilters: { city: city.name },
    faqItems: commonFaqs(city.name),
    priority: city.slug === "medellin" ? 0.8 : 0.75,
    indexWhenEmpty: isMajor,
  });
}

function buildMedellinBodyTypePage(bodyKey, pathPrefix, bodyType) {
  const copy = BODY_TYPE_MEDELLIN_COPY[bodyKey];
  const path = `/${pathPrefix}-medellin`;
  const title =
    bodyKey === "camionetas"
      ? "Camionetas usadas en Medellín"
      : bodyKey === "pickups"
        ? "Pickups usadas en Medellín"
        : bodyKey === "familiares"
          ? "Carros familiares en Medellín"
          : "Automóviles usados en Medellín";
  return baseConfig({
    id: `m26-body-${bodyKey}-medellin`,
    pageType: SEO_PAGE_TYPES.CITY_BODY_TYPE,
    slug: `${bodyKey}-medellin`,
    path,
    title,
    h1: title,
    metaTitle: `${title} | AutoMapa`,
    metaDescription: copy.meta,
    introCopy: copy.intro,
    city: MEDELLIN.name,
    citySlug: "medellin",
    bodyType: bodyType.name,
    bodyTypeSlug: bodyType.slug,
    inventoryFilters: { city: MEDELLIN.name, body_type: bodyType.filterValue },
    faqItems: commonFaqs(MEDELLIN.name),
    priority: 0.7,
    indexWhenEmpty: true,
  });
}

function buildCityFinancingPage(city) {
  const path = `/carros-financiados-${city.slug}`;
  return baseConfig({
    id: `city-financing-${city.slug}`,
    pageType: SEO_PAGE_TYPES.CITY_FINANCING,
    slug: `financiados-${city.slug}`,
    path,
    title: `Carros financiados en ${city.name}`,
    h1: `Carros financiados en ${city.name}`,
    metaTitle: `Carros financiados en ${city.name} | AutoMapa`,
    metaDescription: `Explora carros usados en ${city.name} con posibilidad de financiación. Compara inventario y contacta concesionarios en AutoMapa.`,
    introCopy: `Busca carros usados en ${city.name} y consulta financiación con el concesionario. ${FINANCING_DISCLAIMER}`,
    city: city.name,
    citySlug: city.slug,
    financingRequired: true,
    inventoryFilters: { city: city.name, financing: true },
    faqItems: [
      ...commonFaqs(city.name).slice(0, 3),
      {
        question: "¿AutoMapa ofrece financiación?",
        answer:
          "No. AutoMapa conecta compradores con concesionarios. La financiación se gestiona directamente con el concesionario o entidad crediticia.",
      },
    ],
    priority: 0.7,
    indexWhenEmpty: city.slug === "medellin",
  });
}

function buildCityTransmissionPage(city) {
  const path = `/carros-automaticos-${city.slug}`;
  return baseConfig({
    id: `city-transmission-${city.slug}`,
    pageType: SEO_PAGE_TYPES.CITY_TRANSMISSION,
    slug: `automaticos-${city.slug}`,
    path,
    title: `Carros automáticos usados en ${city.name}`,
    h1: `Carros automáticos usados en ${city.name}`,
    metaTitle: `Carros automáticos usados en ${city.name} | AutoMapa`,
    metaDescription: `Encuentra carros usados automáticos en ${city.name}. Compara precios y concesionarios verificados en AutoMapa.`,
    introCopy: `Filtra carros usados con transmisión automática en ${city.name}. Ideal para tráfico urbano y recorridos frecuentes en el área metropolitana.`,
    city: city.name,
    citySlug: city.slug,
    transmission: "automatic",
    inventoryFilters: { city: city.name, transmission: "automatic" },
    faqItems: commonFaqs(city.name),
    priority: 0.7,
    indexWhenEmpty: city.slug === "medellin",
  });
}

function buildCityEconomyPage(city) {
  const path = `/carros-economicos-${city.slug}`;
  return baseConfig({
    id: `city-economy-${city.slug}`,
    pageType: SEO_PAGE_TYPES.CITY_ECONOMY,
    slug: `economicos-${city.slug}`,
    path,
    title: `Carros económicos usados en ${city.name}`,
    h1: `Carros económicos usados en ${city.name}`,
    metaTitle: `Carros económicos usados en ${city.name} | AutoMapa`,
    metaDescription: `Encuentra carros usados económicos en ${city.name}. Opciones hasta 30 millones con señales de confianza en AutoMapa.`,
    introCopy: `Explora carros usados económicos en ${city.name}, incluyendo compactos y sedanes en rangos accesibles. Prioriza documentos vigentes y revisión mecánica aunque el precio sea bajo.`,
    city: city.name,
    citySlug: city.slug,
    inventoryFilters: { city: city.name, price_max: 30_000_000 },
    budgetMaxCop: 30_000_000,
    faqItems: commonFaqs(city.name),
    priority: 0.65,
    indexWhenEmpty: city.slug === "medellin",
  });
}

function buildMakeCityPage(make, city) {
  const path = `/marcas/${make.slug}/${city.slug}`;
  const title = `${make.name} usados en ${city.name}`;
  return baseConfig({
    id: `make-city-${make.slug}-${city.slug}`,
    pageType: SEO_PAGE_TYPES.MAKE_CITY,
    slug: `${make.slug}-${city.slug}`,
    path,
    title,
    h1: title,
    metaTitle: `${title} | AutoMapa`,
    metaDescription: `Compara ${make.name} usados en ${city.name}. Precios, kilometraje y concesionarios verificados en AutoMapa.`,
    introCopy: BRAND_MEDELLIN_INTRO[make.slug] || `Encuentra ${make.name} usados en ${city.name} con AutoMapa.`,
    make: make.name,
    makeSlug: make.slug,
    city: city.name,
    citySlug: city.slug,
    inventoryFilters: { make: make.name, city: city.name },
    faqItems: commonFaqs(city.name),
    priority: 0.7,
    approved: true,
  });
}

function buildModelCityPage(entry, city) {
  const path = `/modelos/${entry.makeSlug}/${entry.modelSlug}/${city.slug}`;
  const title = `${entry.makeName} ${entry.modelName} usados en ${city.name}`;
  const introKey = `${entry.makeSlug}/${entry.modelSlug}`;
  return baseConfig({
    id: `model-city-${entry.makeSlug}-${entry.modelSlug}-${city.slug}`,
    pageType: SEO_PAGE_TYPES.MODEL_CITY,
    slug: `${entry.makeSlug}-${entry.modelSlug}-${city.slug}`,
    path,
    title,
    h1: title,
    metaTitle: `${title} | AutoMapa`,
    metaDescription: `Compara ${entry.makeName} ${entry.modelName} usados en ${city.name}. Inventario activo y señales de confianza en AutoMapa.`,
    introCopy: MODEL_MEDELLIN_INTRO[introKey] || `Explora ${entry.makeName} ${entry.modelName} usados en ${city.name}.`,
    make: entry.makeName,
    makeSlug: entry.makeSlug,
    model: entry.modelName,
    modelSlug: entry.modelSlug,
    city: city.name,
    citySlug: city.slug,
    inventoryFilters: { make: entry.makeName, model: entry.modelName, city: city.name },
    faqItems: commonFaqs(city.name),
    priority: 0.7,
    approved: true,
  });
}

function buildBudgetCityPage(budget, city) {
  const path = `/presupuesto/${budget.slug}/${city.slug}`;
  const title = `Carros usados de ${budget.label.toLowerCase()} en ${city.name}`;
  return baseConfig({
    id: `budget-city-${budget.slug}-${city.slug}`,
    pageType: SEO_PAGE_TYPES.BUDGET_CITY,
    slug: `${budget.slug}-${city.slug}`,
    path,
    title,
    h1: title,
    metaTitle: `${title} | AutoMapa`,
    metaDescription: `Encuentra carros usados de ${budget.label.toLowerCase()} en ${city.name}. Compara opciones y contacta concesionarios en AutoMapa.`,
    introCopy: `${BUDGET_MEDELLIN_GUIDANCE[budget.slug] || ""} Explora inventario en ${city.name} dentro de este rango de precio.`,
    city: city.name,
    citySlug: city.slug,
    budgetMinCop: budget.minCop,
    budgetMaxCop: budget.maxCop,
    budgetSlug: budget.slug,
    inventoryFilters: {
      city: city.name,
      price_min: budget.minCop > 0 ? budget.minCop : undefined,
      price_max: budget.maxCop || undefined,
    },
    faqItems: commonFaqs(city.name),
    priority: 0.6,
    indexWhenEmpty: city.slug === "medellin",
  });
}

function buildDealerCityPage(city) {
  const path = `/concesionarios/${city.slug}`;
  return baseConfig({
    id: `dealer-city-${city.slug}`,
    pageType: SEO_PAGE_TYPES.DEALER_CITY,
    slug: city.slug,
    path,
    title: `Concesionarios en ${city.name}`,
    h1: `Concesionarios verificados en ${city.name}`,
    metaTitle: `Concesionarios en ${city.name} | AutoMapa`,
    metaDescription: `Encuentra concesionarios verificados en ${city.name} con inventario actualizado y contacto por WhatsApp en AutoMapa.`,
    introCopy: `Explora concesionarios con presencia en ${city.name}. “Verificado por AutoMapa” significa revisión básica de datos del negocio y contacto; no garantiza el estado de cada vehículo.`,
    city: city.name,
    citySlug: city.slug,
    inventoryFilters: { city: city.name },
    faqItems: [
      {
        question: `¿Cómo elijo un concesionario en ${city.name}?`,
        answer:
          "Revisa badge de verificación, inventario reciente, tiempos de respuesta y señales en publicaciones antes de visitar o pagar.",
      },
      ...commonFaqs(city.name).slice(2, 5),
    ],
    priority: 0.7,
    indexWhenEmpty: true,
  });
}

function buildGuidePage(guide) {
  return baseConfig({
    id: `guide-${guide.slug}`,
    pageType: SEO_PAGE_TYPES.GUIDE,
    slug: guide.slug,
    path: `/guias/${guide.slug}`,
    title: guide.title,
    h1: guide.title,
    metaTitle: guide.metaTitle || `${guide.title} | AutoMapa`,
    metaDescription: guide.metaDescription,
    introCopy: guide.introCopy,
    guideLinks: guide.relatedGuides || [],
    faqItems: guide.faqItems || [],
    inventoryFilters: guide.inventoryFilters || {},
    noindex: guide.noindex || false,
    includeInSitemap: true,
    priority: guide.priority || 0.7,
    guideContent: guide,
  });
}

function buildM26ContentSet() {
  const pages = [];
  if (!MEDELLIN) return pages;

  const bodyTypeMap = {
    camionetas: SEO_BODY_TYPES.find((b) => b.slug === "camionetas"),
    automoviles: SEO_BODY_TYPES.find((b) => b.slug === "automoviles"),
    pickups: SEO_BODY_TYPES.find((b) => b.slug === "pickups"),
    familiares: SEO_BODY_TYPES.find((b) => b.slug === "familiares"),
  };

  M26_ANTIOQUIA_CITY_SLUGS.forEach((slug) => {
    const city = findCity(slug);
    if (city) pages.push(buildCityPage(city));
  });

  pages.push(buildMedellinBodyTypePage("camionetas", "camionetas-usadas", bodyTypeMap.camionetas));
  pages.push(buildMedellinBodyTypePage("automoviles", "automoviles-usados", bodyTypeMap.automoviles));
  pages.push(buildMedellinBodyTypePage("pickups", "pickups-usadas", bodyTypeMap.pickups));
  pages.push(buildMedellinBodyTypePage("familiares", "carros-familiares", bodyTypeMap.familiares));

  pages.push(buildCityFinancingPage(MEDELLIN));
  pages.push(buildCityTransmissionPage(MEDELLIN));
  pages.push(buildCityEconomyPage(MEDELLIN));

  M26_MEDELLIN_BUDGET_SLUGS.forEach((slug) => {
    const budget = findBudget(slug);
    if (budget) pages.push(buildBudgetCityPage(budget, MEDELLIN));
  });

  M26_MEDELLIN_BRAND_SLUGS.forEach((slug) => {
    const make = findMake(slug);
    if (make) pages.push(buildMakeCityPage(make, MEDELLIN));
  });

  M26_MEDELLIN_MODELS.forEach((entry) => {
    pages.push(buildModelCityPage(entry, MEDELLIN));
  });

  pages.push(buildDealerCityPage(MEDELLIN));

  M26_GUIDES.forEach((guide) => pages.push(buildGuidePage(guide)));

  return pages.map((page) => ({
    ...page,
    canonicalUrl: page.canonicalUrl || page.path,
  }));
}

export const SEO_PAGE_REGISTRY = buildM26ContentSet();

let seoPathIndex = null;

/** Safe accessor for consumers that load this module through circular import graphs. */
export function getSeoPathIndex() {
  if (!seoPathIndex) {
    seoPathIndex = new Map(SEO_PAGE_REGISTRY.map((page) => [page.path, page]));
  }
  return seoPathIndex;
}

export function getRegistryNearbyCities(citySlug) {
  return (SEO_NEARBY_CITIES[citySlug] || []).map(
    (slug) => SEO_CITIES.find((city) => city.slug === slug)?.name
  ).filter(Boolean);
}

export { SEO_CITIES, SEO_POPULAR_MAKES, SEO_BUDGET_RANGES, SEO_BODY_TYPES };
